/**
 * Cola de planificación.
 *
 * Agrupada por centro porque **se visita un centro, no un extintor**:
 * mandar un técnico a un hotel por un solo equipo vencido es lo que
 * arruina la rentabilidad de un contrato.
 *
 * El filtro «sin contrato» es el hallazgo comercial más rentable del
 * sistema: una instalación con obligaciones de mantenimiento que nadie
 * está manteniendo.
 */

import { useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';

interface PlanningRow {
  id: string;
  site_id: string;
  site_code: string;
  site_name: string;
  customer_name: string;
  due_count: string;
  overdue_count: string;
  unplannable_count: string;
  earliest_due_date: string;
  days_to_earliest: number;
  has_contract: boolean;
}

const COLUMNS: Column<PlanningRow>[] = [
  { id: 'siteCode', label: 'Código', render: (r) => r.site_code, width: '7rem' },
  { id: 'siteName', label: 'Centro', render: (r) => r.site_name, sortable: true },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  {
    id: 'earliestDueDate',
    label: 'Primer vencimiento',
    width: '15rem',
    sortable: true,
    render: (r) =>
      r.days_to_earliest < 0 ? (
        <span className="due due--late">
          ⚠ Vencido hace {Math.abs(r.days_to_earliest)} d · {r.earliest_due_date}
        </span>
      ) : r.days_to_earliest <= 30 ? (
        <span className="due due--soon">
          ◷ {r.days_to_earliest} d · {r.earliest_due_date}
        </span>
      ) : (
        <span className="due">{r.earliest_due_date}</span>
      ),
    exportValue: (r) => r.earliest_due_date,
  },
  {
    id: 'dueCount',
    label: 'Obligaciones',
    numeric: true,
    width: '8rem',
    sortable: true,
    render: (r) => r.due_count,
  },
  {
    id: 'overdueCount',
    label: 'Ya vencidas',
    numeric: true,
    width: '8rem',
    render: (r) =>
      Number(r.overdue_count) > 0 ? (
        <span className="due due--late">⚠ {r.overdue_count}</span>
      ) : (
        <span className="due--none">0</span>
      ),
    exportValue: (r) => r.overdue_count,
  },
  {
    id: 'unplannableCount',
    label: 'Sin planificar',
    numeric: true,
    width: '9rem',
    /**
     * Equipos en servicio que no generan obligación porque les falta la
     * fecha de puesta en servicio. No es una cifra menor: mientras esté
     * ahí, ese equipo no aparece en ninguna orden de trabajo.
     */
    render: (r) =>
      Number(r.unplannable_count) > 0 ? (
        <span className="due due--late">⚠ {r.unplannable_count}</span>
      ) : (
        <span className="due--none">0</span>
      ),
    exportValue: (r) => r.unplannable_count,
  },
  {
    id: 'hasContract',
    label: 'Contrato',
    width: '11rem',
    render: (r) =>
      r.has_contract ? (
        <span className="pill pill--ok">✓ Con contrato</span>
      ) : (
        <span className="pill pill--bad">⚠ Sin contrato</span>
      ),
    exportValue: (r) => (r.has_contract ? 'Con contrato' : 'Sin contrato'),
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'all', label: 'Toda la cola', param: {} },
  { id: 'overdue', label: 'Con vencidos', param: { overdue: 'true' } },
  { id: 'nocontract', label: 'Sin contrato', param: { withoutContract: 'true' } },
  { id: 'unplannable', label: 'Sin planificar', param: { unplannable: 'true' } },
] as const;

export function PlanningScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<PlanningRow>({
    path: '/planning/queue',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Planificación</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'all' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listing.total !== null && <span className="screen__count">{listing.total} centros</span>}
      </header>

      {quick.id === 'nocontract' && (
        <p className="screen__note">
          Instalaciones con obligaciones de mantenimiento y sin contrato activo. Cada fila es un
          incumplimiento del titular y una oportunidad comercial documentada.
        </p>
      )}

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName={quick.id === 'nocontract' ? 'centros-sin-contrato' : 'planificacion'}
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay centros con obligaciones pendientes."
      />
    </section>
  );
}
